import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { queryKeys } from '@/lib/queryKeys'
import {
  createReportFilter,
  deleteReportFilter,
  fetchReportFilters,
} from '@/api/reportFilters'
import type {
  SavedReportFilterCreate,
  SavedReportFilterRead,
} from '@/types/reportFilters'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

export function useReportFilters(planId: string, reportType: string | null) {
  return useQuery({
    queryKey: queryKeys.reportFilters.list(planId, reportType ?? ''),
    queryFn: () => fetchReportFilters(planId, reportType as string),
    enabled: Boolean(planId && reportType),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateReportFilter(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: SavedReportFilterCreate) =>
      createReportFilter(planId, payload),
    onSuccess: (data: SavedReportFilterRead) => {
      queryClient.setQueryData<SavedReportFilterRead[]>(
        queryKeys.reportFilters.list(planId, data.report_type),
        existing => (existing ? [...existing, data] : [data])
      )
      toast.success('Filter saved')
    },
    onError: (error: Error) => {
      const isDuplicate =
        error instanceof AxiosError && error.response?.status === 409
      toast.error(
        isDuplicate
          ? 'A saved filter with that name already exists for this report.'
          : getErrorMessage(error, 'Failed to save filter')
      )
    },
  })
}

export function useDeleteReportFilter(planId: string, reportType: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (filterId: string) => deleteReportFilter(planId, filterId),
    onSuccess: (_data, filterId) => {
      queryClient.setQueryData<SavedReportFilterRead[]>(
        queryKeys.reportFilters.list(planId, reportType),
        existing => existing?.filter(f => f.id !== filterId)
      )
      toast.success('Filter deleted')
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error, 'Failed to delete filter'))
    },
  })
}
